import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getActiveRecurringTemplates } from '@/lib/firestore-recurring';
import { processAllRecurringTemplates } from '@/lib/recurring-processor';

// Module-level lock — prevents two tabs or rapid re-mounts from running simultaneously
let isProcessing = false;
let lastRunTimestamp = 0;
const MIN_RUN_INTERVAL_MS = 60 * 1000; // Don't run more than once per minute

/**
 * Hook to automatically process recurring transactions.
 * Mounted once in App.tsx so it runs on every page, not just Dashboard.
 */
export function useRecurringTransactionProcessor() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    let isActive = true;

    const processRecurring = async () => {
      // Prevent concurrent runs (two tabs, fast navigation, React StrictMode double-mount)
      if (isProcessing) {
        console.log('⏳ Recurring processor already running, skipping...');
        return;
      }

      // Throttle — don't run more than once per minute
      const now = Date.now();
      if (now - lastRunTimestamp < MIN_RUN_INTERVAL_MS) {
        return;
      }

      isProcessing = true;
      lastRunTimestamp = now;

      try {
        const templates = await getActiveRecurringTemplates(user.uid);
        if (!isActive || templates.length === 0) return;

        const result = await processAllRecurringTemplates(templates);

        if (result.processed > 0) {
          console.log(`✅ Recurring: created ${result.processed} transaction(s)`);
        }
        if (result.failed > 0) {
          console.error(`❌ Recurring: ${result.failed} template(s) failed`);
        }
      } catch (error) {
        console.error('Error in recurring processor:', error);
      } finally {
        isProcessing = false;
      }
    };

    // Run immediately on mount
    processRecurring();

    // Re-run every hour
    const intervalId = setInterval(() => {
      if (isActive) processRecurring();
    }, 60 * 60 * 1000);

    return () => {
      isActive = false;
      clearInterval(intervalId);
    };
  }, [user]);
}
